import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
  Briefcase, Plus, Search, MapPin, Clock, CheckCircle2, AlertTriangle,
  User, Trash2, RefreshCw, Navigation, Calendar, ClipboardList,
  ArrowRight, Play, Phone, Star, ChevronDown, ChevronUp, FileText,
  MapPinned, Timer, TrendingUp, XCircle, WifiOff, Wifi, CloudUpload
} from 'lucide-react';
import { useOfflineMode } from '../hooks/useOfflineMode';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';
import { RouteErrorBoundary } from '../components/RouteErrorBoundary';

const API = process.env.REACT_APP_BACKEND_URL;
// 2026-02-11 (audit P0-1) — Bounded fetch timeout so no async surface hangs.
const FETCH_TIMEOUT_MS = 15000;

const STATUS_CONFIG = {
  new:          { label: 'New',         color: 'bg-slate-100 text-slate-700',   icon: ClipboardList },
  assigned:     { label: 'Assigned',    color: 'bg-blue-100 text-blue-700',     icon: User },
  en_route:     { label: 'En Route',    color: 'bg-purple-100 text-purple-700', icon: Navigation },
  in_progress:  { label: 'In Progress', color: 'bg-amber-100 text-amber-700',   icon: Play },
  completed:    { label: 'Completed',   color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  verified:     { label: 'Verified',    color: 'bg-teal-100 text-teal-700',     icon: Star },
  cancelled:    { label: 'Cancelled',   color: 'bg-red-100 text-red-700',       icon: XCircle },
};

const PRIORITY_CONFIG = {
  low:    { label: 'Low',    color: 'bg-gray-100 text-gray-600', dot: 'bg-gray-400' },
  normal: { label: 'Normal', color: 'bg-blue-100 text-blue-600', dot: 'bg-blue-400' },
  high:   { label: 'High',   color: 'bg-orange-100 text-orange-600', dot: 'bg-orange-400' },
  urgent: { label: 'Urgent', color: 'bg-red-100 text-red-600', dot: 'bg-red-500' },
};

/* ── Stat Card ──────────────────────────────────────────────────────── */
function StatCard({ icon: Icon, label, value, color, accent }) {
  return (
    <Card className="bg-white border-gray-border" data-testid={`field-stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="p-3 sm:p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-gray-medium">{label}</p>
            <p className={cn("text-xl sm:text-2xl font-bold mt-0.5", accent || "text-text-primary")}>{value}</p>
          </div>
          <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center", color)}>
            <Icon className="h-4 w-4" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/* ── Job Card ───────────────────────────────────────────────────────── */
function JobCard({ job, onClick }) {
  const sc = STATUS_CONFIG[job.status] || STATUS_CONFIG.new;
  const pc = PRIORITY_CONFIG[job.priority] || PRIORITY_CONFIG.normal;
  return (
    <Card
      className="bg-white border-gray-border hover:border-gray-300 hover:shadow-sm transition-all cursor-pointer"
      onClick={() => onClick(job)}
      data-testid={`job-card-${job.id}`}
    >
      <CardContent className="p-3 sm:p-4">
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="font-medium text-sm text-text-primary leading-tight line-clamp-2">{job.title}</h3>
          <Badge className={cn("text-[10px] flex-shrink-0 gap-1", sc.color)}>
            <sc.icon className="h-3 w-3" />
            {sc.label}
          </Badge>
        </div>
        {job.address && (
          <div className="flex items-center gap-1.5 text-xs text-gray-medium mb-1.5">
            <MapPin className="h-3 w-3 flex-shrink-0" />
            <span className="truncate">{job.address}</span>
          </div>
        )}
        <div className="flex items-center gap-3 text-xs text-gray-400 flex-wrap">
          {job.assigned_name && (
            <span className="flex items-center gap-1"><User className="h-3 w-3" />{job.assigned_name}</span>
          )}
          {job.scheduled_date && (
            <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{job.scheduled_date}</span>
          )}
          {job.scheduled_time && (
            <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{job.scheduled_time}</span>
          )}
          <Badge className={cn("text-[10px]", pc.color)}>
            <span className={cn("w-1.5 h-1.5 rounded-full mr-1", pc.dot)} />
            {pc.label}
          </Badge>
        </div>
        {job.contact_name && (
          <div className="flex items-center gap-1.5 text-xs text-gray-400 mt-1.5">
            <Phone className="h-3 w-3" />
            <span>{job.contact_name}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ── Create/Edit Job Dialog ──────────────────────────────────────────── */
function JobDialog({ open, onClose, onSaved, job, staff, templates }) {
  const isEdit = !!job;
  const [form, setForm] = useState({
    title: '', description: '', job_type: 'general', template_id: '',
    assigned_to: '', assigned_name: '', client_id: '', contact_name: '', address: '',
    priority: 'normal', scheduled_date: '', scheduled_time: '',
    estimated_duration_min: 60, notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);

  useEffect(() => {
    if (open) {
      axios.get(`${API}/api/clients/dropdown`).then(r => setClients(r.data)).catch(() => {});
    }
  }, [open]);

  useEffect(() => {
    if (job) {
      setForm({
        title: job.title || '', description: job.description || '',
        job_type: job.job_type || 'general', template_id: job.template_id || '',
        assigned_to: job.assigned_to || '', assigned_name: job.assigned_name || '',
        client_id: job.client_id || '', contact_name: job.contact_name || '', address: job.address || '',
        priority: job.priority || 'normal', scheduled_date: job.scheduled_date || '',
        scheduled_time: job.scheduled_time || '',
        estimated_duration_min: job.estimated_duration_min || 60, notes: job.notes || '',
      });
      if (job.client_id) {
        const c = clients.find(c => c.id === job.client_id);
        if (c) setSelectedClient(c);
      }
    } else {
      setForm({ title: '', description: '', job_type: 'general', template_id: '', assigned_to: '', assigned_name: '', client_id: '', contact_name: '', address: '', priority: 'normal', scheduled_date: '', scheduled_time: '', estimated_duration_min: 60, notes: '' });
      setSelectedClient(null);
    }
  }, [job, open, clients]);

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await axios.put(`${API}/api/field/jobs/${job.id}`, form);
        toast.success('Job updated');
      } else {
        await axios.post(`${API}/api/field/jobs`, form);
        toast.success('Job created');
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save job');
    } finally {
      setSaving(false);
    }
  };

  const handleStaffChange = (id) => {
    const s = staff.find(s => s.id === id);
    setForm(f => ({ ...f, assigned_to: id, assigned_name: s?.name || '' }));
  };

  const handleClientChange = (clientId) => {
    if (clientId === 'none') {
      setSelectedClient(null);
      setForm(f => ({ ...f, client_id: '', contact_name: '', address: '' }));
      return;
    }
    const c = clients.find(c => c.id === clientId);
    if (c) {
      setSelectedClient(c);
      const name = c.company_name ? `${c.contact_name} (${c.company_name})` : c.contact_name;
      const addr = c.address || '';
      setForm(f => ({ ...f, client_id: c.id, contact_name: name, address: addr }));
    }
  };

  const handleLocationChange = (locLabel) => {
    if (!selectedClient) return;
    if (locLabel === '_primary') {
      setForm(f => ({ ...f, address: selectedClient.address || '' }));
      return;
    }
    const loc = (selectedClient.locations || []).find(l => l.label === locLabel);
    if (loc) {
      const full = [loc.address, loc.city, loc.country].filter(Boolean).join(', ');
      setForm(f => ({ ...f, address: full }));
    }
  };

  const clientLocations = selectedClient?.locations || [];
  const hasMultipleLocations = clientLocations.length > 0;

  const allTemplates = [...(templates?.prebuilt || []), ...(templates?.custom || [])];

  const DURATION_OPTIONS = [
    { value: 30, label: '30 min' }, { value: 60, label: '1 hour' }, { value: 90, label: '1.5 hours' },
    { value: 120, label: '2 hours' }, { value: 180, label: '3 hours' }, { value: 240, label: '4 hours' },
    { value: 480, label: 'Full day' },
  ];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="job-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Job' : 'Create New Job'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {/* Template + Priority */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Job Template *</Label>
              <Select value={form.template_id || 'none'} onValueChange={v => {
                const tpl = allTemplates.find(t => (t.id || t.name) === v);
                setForm(f => ({
                  ...f,
                  template_id: v === 'none' ? '' : v,
                  job_type: tpl?.industry || 'general',
                  title: tpl ? tpl.name : f.title,
                }));
              }}>
                <SelectTrigger data-testid="job-template-select"><SelectValue placeholder="Select job type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select job type...</SelectItem>
                  {allTemplates.map(t => (
                    <SelectItem key={t.id || t.name} value={t.id || t.name}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Priority *</Label>
              <Select value={form.priority} onValueChange={v => setForm(f => ({ ...f, priority: v }))}>
                <SelectTrigger data-testid="job-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(PRIORITY_CONFIG).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Title (auto-filled from template, editable) */}
          <div>
            <Label>Job Title *</Label>
            <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Auto-filled from template or type here" data-testid="job-title-input" />
          </div>

          {/* Customer Dropdown */}
          <div>
            <Label>Customer *</Label>
            <Select value={form.client_id || 'none'} onValueChange={handleClientChange}>
              <SelectTrigger data-testid="job-client-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Select customer...</SelectItem>
                {clients.map(c => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.company_name ? `${c.contact_name} (${c.company_name})` : c.contact_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Location — auto-populated, dropdown if multiple */}
          {selectedClient && (
            <div>
              <Label>Service Location *</Label>
              {hasMultipleLocations ? (
                <Select value="_select" onValueChange={handleLocationChange}>
                  <SelectTrigger data-testid="job-location-select"><SelectValue placeholder={form.address || 'Select location'} /></SelectTrigger>
                  <SelectContent>
                    {selectedClient.address && (
                      <SelectItem value="_primary">Primary: {selectedClient.address}</SelectItem>
                    )}
                    {clientLocations.map((loc, i) => (
                      <SelectItem key={i} value={loc.label}>
                        {loc.label}: {loc.address}{loc.city ? `, ${loc.city}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input value={form.address} readOnly className="bg-gray-50" data-testid="job-address-input" />
              )}
              {!hasMultipleLocations && !form.address && (
                <p className="text-[10px] text-amber-500 mt-0.5">No address on file. Add locations in client profile.</p>
              )}
            </div>
          )}
          {!selectedClient && (
            <div>
              <Label>Address</Label>
              <Input value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} placeholder="Select a customer first" data-testid="job-address-input" />
            </div>
          )}

          {/* Assign To */}
          <div>
            <Label>Assign To</Label>
            <Select value={form.assigned_to || 'unassigned'} onValueChange={handleStaffChange}>
              <SelectTrigger data-testid="job-assign-select"><SelectValue placeholder="Select staff member" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                {staff.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.name} ({s.role})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Schedule Row — all dropdowns */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Date</Label>
              <Input type="date" value={form.scheduled_date} onChange={e => setForm(f => ({ ...f, scheduled_date: e.target.value }))} data-testid="job-date-input" />
            </div>
            <div>
              <Label>Time</Label>
              <Select value={form.scheduled_time || 'any'} onValueChange={v => setForm(f => ({ ...f, scheduled_time: v === 'any' ? '' : v }))}>
                <SelectTrigger><SelectValue placeholder="Any time" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any Time</SelectItem>
                  {['08:00','09:00','10:00','11:00','12:00','13:00','14:00','15:00','16:00','17:00','18:00'].map(t => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Duration</Label>
              <Select value={String(form.estimated_duration_min)} onValueChange={v => setForm(f => ({ ...f, estimated_duration_min: parseInt(v) }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DURATION_OPTIONS.map(d => (
                    <SelectItem key={d.value} value={String(d.value)}>{d.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Description + Notes */}
          <div>
            <Label>Description</Label>
            <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Job details..." rows={2} />
          </div>
          <div>
            <Label>Internal Notes</Label>
            <Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} placeholder="Internal notes..." />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-job-btn">
            {saving ? 'Saving...' : isEdit ? 'Update Job' : 'Create Job'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ── Job Detail Panel ──────────────────────────────────────────────── */
function JobDetail({ job, onClose, onRefresh, templates }) {
  const [updating, setUpdating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({});
  const [completionNotes, setCompletionNotes] = useState('');
  const [customerRating, setCustomerRating] = useState(0);

  if (!job) return null;

  const sc = STATUS_CONFIG[job.status] || STATUS_CONFIG.new;
  const pc = PRIORITY_CONFIG[job.priority] || PRIORITY_CONFIG.normal;

  // Find template fields
  const allTemplates = [...(templates?.prebuilt || []), ...(templates?.custom || [])];
  const tpl = allTemplates.find(t => (t.id || t.name) === job.template_id);
  const templateFields = tpl?.fields || [];

  const updateStatus = async (newStatus) => {
    setUpdating(true);
    try {
      await axios.put(`${API}/api/field/jobs/${job.id}`, { status: newStatus });
      toast.success(`Status updated to ${STATUS_CONFIG[newStatus]?.label}`);
      onRefresh();
    } catch (e) {
      toast.error('Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const handleCheckin = async () => {
    setUpdating(true);
    try {
      // Try to get GPS location
      let lat = 0, lng = 0;
      try {
        const pos = await new Promise((resolve, reject) => navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 }));
        lat = pos.coords.latitude;
        lng = pos.coords.longitude;
      } catch { /* GPS unavailable, send 0,0 */ }
      await axios.post(`${API}/api/field/jobs/${job.id}/checkin`, { latitude: lat, longitude: lng });
      toast.success('Checked in!');
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Check-in failed');
    } finally {
      setUpdating(false);
    }
  };

  const handleCheckout = async () => {
    setUpdating(true);
    try {
      let lat = 0, lng = 0;
      try {
        const pos = await new Promise((resolve, reject) => navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 }));
        lat = pos.coords.latitude;
        lng = pos.coords.longitude;
      } catch { /* GPS unavailable */ }
      await axios.post(`${API}/api/field/jobs/${job.id}/checkout`, {
        latitude: lat, longitude: lng,
        form_data: formData,
        customer_rating: customerRating || null,
        completion_notes: completionNotes,
      });
      toast.success('Job completed!');
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Check-out failed');
    } finally {
      setUpdating(false);
    }
  };

  const statusFlow = ['new', 'assigned', 'en_route', 'in_progress', 'completed', 'verified'];
  const currentIdx = statusFlow.indexOf(job.status);

  return (
    <Dialog open={!!job} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="job-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Briefcase className="h-4 w-4" />
            {job.title}
          </DialogTitle>
        </DialogHeader>

        {/* Status Flow */}
        <div className="flex items-center gap-1 overflow-x-auto pb-2" data-testid="status-flow">
          {statusFlow.map((s, i) => {
            const cfg = STATUS_CONFIG[s];
            const active = i <= currentIdx;
            return (
              <div key={s} className="flex items-center gap-1">
                <div className={cn("px-2 py-1 rounded-full text-[10px] font-medium whitespace-nowrap", active ? cfg.color : "bg-gray-50 text-gray-300")}>
                  {cfg.label}
                </div>
                {i < statusFlow.length - 1 && <ArrowRight className={cn("h-3 w-3 flex-shrink-0", active ? "text-gray-400" : "text-gray-200")} />}
              </div>
            );
          })}
        </div>

        {/* Job Details */}
        <div className="space-y-3 text-sm">
          {job.description && <p className="text-gray-600">{job.description}</p>}
          <div className="grid grid-cols-2 gap-3">
            {job.address && (
              <div className="flex items-start gap-2"><MapPin className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" /><span>{job.address}</span></div>
            )}
            {job.assigned_name && (
              <div className="flex items-center gap-2"><User className="h-4 w-4 text-gray-400" /><span>{job.assigned_name}</span></div>
            )}
            {job.scheduled_date && (
              <div className="flex items-center gap-2"><Calendar className="h-4 w-4 text-gray-400" /><span>{job.scheduled_date} {job.scheduled_time || ''}</span></div>
            )}
            {job.contact_name && (
              <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-gray-400" /><span>{job.contact_name}</span></div>
            )}
          </div>
          <div className="flex gap-2">
            <Badge className={cn("text-xs", pc.color)}>{pc.label} Priority</Badge>
            <Badge className={cn("text-xs", sc.color)}>{sc.label}</Badge>
          </div>
          {job.checkin_time && (
            <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-2.5 text-xs">
              <p className="font-medium text-emerald-700">Checked In: {new Date(job.checkin_time).toLocaleString()}</p>
              {job.checkin_lat ? <p className="text-emerald-600 mt-0.5">GPS: {job.checkin_lat.toFixed(5)}, {job.checkin_lng.toFixed(5)}</p> : null}
            </div>
          )}
          {job.checkout_time && (
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-2.5 text-xs">
              <p className="font-medium text-blue-700">Checked Out: {new Date(job.checkout_time).toLocaleString()}</p>
              {job.customer_rating ? <p className="text-blue-600 mt-0.5">Customer Rating: {'★'.repeat(job.customer_rating)}{'☆'.repeat(5 - job.customer_rating)}</p> : null}
            </div>
          )}
          {job.notes && (
            <div className="text-xs text-gray-500 bg-gray-50 rounded-lg p-2.5"><strong>Notes:</strong> {job.notes}</div>
          )}
          {/* Completed form data */}
          {job.form_data && Object.keys(job.form_data).length > 0 && (
            <div className="bg-gray-50 rounded-lg p-3 text-xs space-y-1.5">
              <p className="font-medium text-gray-700">Completion Form Data</p>
              {Object.entries(job.form_data).map(([k, v]) => (
                <div key={k} className="flex justify-between"><span className="text-gray-500">{k}</span><span className="font-medium">{Array.isArray(v) ? v.join(', ') : String(v)}</span></div>
              ))}
            </div>
          )}
        </div>

        {/* Action Buttons based on status */}
        <div className="space-y-3 pt-2">
          {job.status === 'new' && (
            <Button className="w-full" onClick={() => updateStatus('assigned')} disabled={updating} data-testid="assign-job-btn">
              <User className="h-4 w-4 mr-2" />Assign Job
            </Button>
          )}
          {job.status === 'assigned' && (
            <Button className="w-full" onClick={() => updateStatus('en_route')} disabled={updating} data-testid="enroute-btn">
              <Navigation className="h-4 w-4 mr-2" />Mark En Route
            </Button>
          )}
          {(job.status === 'en_route' || (job.status === 'assigned' && !job.checkin_time)) && (
            <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={handleCheckin} disabled={updating} data-testid="checkin-btn">
              <MapPinned className="h-4 w-4 mr-2" />Check In (GPS)
            </Button>
          )}
          {job.status === 'in_progress' && !job.checkout_time && (
            <>
              <Button variant="outline" className="w-full" onClick={() => setShowForm(!showForm)} data-testid="toggle-form-btn">
                <FileText className="h-4 w-4 mr-2" />{showForm ? 'Hide' : 'Show'} Completion Form
              </Button>
              {showForm && (
                <div className="space-y-3 bg-gray-50 rounded-lg p-3 border">
                  {templateFields.length > 0 ? templateFields.map(field => (
                    <div key={field.id}>
                      <Label className="text-xs">{field.label} {field.required && '*'}</Label>
                      {field.type === 'text' && <Input value={formData[field.label] || ''} onChange={e => setFormData(d => ({ ...d, [field.label]: e.target.value }))} />}
                      {field.type === 'textarea' && <Textarea value={formData[field.label] || ''} onChange={e => setFormData(d => ({ ...d, [field.label]: e.target.value }))} rows={2} />}
                      {field.type === 'number' && <Input type="number" value={formData[field.label] || ''} onChange={e => setFormData(d => ({ ...d, [field.label]: e.target.value }))} />}
                      {field.type === 'dropdown' && (
                        <Select value={formData[field.label] || ''} onValueChange={v => setFormData(d => ({ ...d, [field.label]: v }))}>
                          <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                          <SelectContent>{field.options?.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                        </Select>
                      )}
                      {field.type === 'checklist' && (
                        <div className="flex flex-wrap gap-2 mt-1">
                          {field.options?.map(o => {
                            const checked = (formData[field.label] || []).includes(o);
                            return (
                              <button key={o} type="button"
                                className={cn("px-2 py-1 rounded text-xs border transition-all", checked ? "bg-emerald-100 border-emerald-300 text-emerald-700" : "bg-white border-gray-200 text-gray-600")}
                                onClick={() => {
                                  const current = formData[field.label] || [];
                                  const next = checked ? current.filter(x => x !== o) : [...current, o];
                                  setFormData(d => ({ ...d, [field.label]: next }));
                                }}
                              >{o}</button>
                            );
                          })}
                        </div>
                      )}
                      {field.type === 'rating' && (
                        <div className="flex gap-1 mt-1">
                          {[1,2,3,4,5].map(n => (
                            <button key={n} type="button" className={cn("text-lg", (formData[field.label] || 0) >= n ? "text-amber-400" : "text-gray-300")}
                              onClick={() => setFormData(d => ({ ...d, [field.label]: n }))}
                            >★</button>
                          ))}
                        </div>
                      )}
                    </div>
                  )) : (
                    <p className="text-xs text-gray-400">No template assigned. Add completion notes below.</p>
                  )}
                  <div>
                    <Label className="text-xs">Completion Notes</Label>
                    <Textarea value={completionNotes} onChange={e => setCompletionNotes(e.target.value)} rows={2} />
                  </div>
                  <div>
                    <Label className="text-xs">Customer Rating</Label>
                    <div className="flex gap-1 mt-1">
                      {[1,2,3,4,5].map(n => (
                        <button key={n} type="button" className={cn("text-xl", customerRating >= n ? "text-amber-400" : "text-gray-300")}
                          onClick={() => setCustomerRating(n)}
                        >★</button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              <Button className="w-full bg-blue-600 hover:bg-blue-700" onClick={handleCheckout} disabled={updating} data-testid="checkout-btn">
                <CheckCircle2 className="h-4 w-4 mr-2" />Complete & Check Out
              </Button>
            </>
          )}
          {job.status === 'completed' && (
            <Button className="w-full bg-teal-600 hover:bg-teal-700" onClick={() => updateStatus('verified')} disabled={updating} data-testid="verify-btn">
              <Star className="h-4 w-4 mr-2" />Verify Completion
            </Button>
          )}
          {!['completed', 'verified', 'cancelled'].includes(job.status) && (
            <Button variant="outline" className="w-full text-red-600 hover:bg-red-50" onClick={() => updateStatus('cancelled')} disabled={updating} data-testid="cancel-job-btn">
              <XCircle className="h-4 w-4 mr-2" />Cancel Job
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}


/* ── Main Page ────────────────────────────────────────────────────── */
function FieldJobsPageInner() {
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState({});
  const [staff, setStaff] = useState([]);
  const [templates, setTemplates] = useState({ prebuilt: [], custom: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [editJob, setEditJob] = useState(null);
  const [detailJob, setDetailJob] = useState(null);
  const [viewMode, setViewMode] = useState('list');
  const [loadError, setLoadError] = useState(null);
  const { isOnline, queueCount, syncing, syncQueue } = useOfflineMode();

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const params = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterPriority !== 'all') params.priority = filterPriority;
      if (search) params.search = search;
      const [jRes, sRes, stRes, tRes] = await Promise.allSettled([
        axios.get(`${API}/api/field/jobs`, { params, timeout: FETCH_TIMEOUT_MS }),
        axios.get(`${API}/api/field/jobs/stats`, { timeout: FETCH_TIMEOUT_MS }),
        axios.get(`${API}/api/field/staff`, { timeout: FETCH_TIMEOUT_MS }),
        axios.get(`${API}/api/field/templates`, { timeout: FETCH_TIMEOUT_MS }),
      ]);
      // Defensive shape guards — never let a bad payload crash the render.
      setJobs(jRes.status === 'fulfilled' && Array.isArray(jRes.value?.data) ? jRes.value.data : []);
      setStats(sRes.status === 'fulfilled' && sRes.value?.data && typeof sRes.value.data === 'object' && !Array.isArray(sRes.value.data) ? sRes.value.data : {});
      setStaff(stRes.status === 'fulfilled' && Array.isArray(stRes.value?.data) ? stRes.value.data : []);
      const td = tRes.status === 'fulfilled' ? tRes.value?.data : null;
      setTemplates(td && typeof td === 'object' && !Array.isArray(td)
        ? { prebuilt: Array.isArray(td.prebuilt) ? td.prebuilt : [], custom: Array.isArray(td.custom) ? td.custom : [] }
        : { prebuilt: [], custom: [] });
      // If the primary jobs call failed, surface a visible error banner (not a blank screen).
      if (jRes.status === 'rejected') {
        const err = jRes.reason;
        const code = err?.response?.status || err?.code || 'network';
        const msg = err?.code === 'ECONNABORTED'
          ? 'Field Jobs took too long to respond. Retry when your connection recovers.'
          : (err?.response?.data?.detail || err?.message || 'Failed to load field jobs.');
        setLoadError({ code, message: msg });
      }
    } catch (e) {
      setLoadError({ code: 'unknown', message: e?.message || 'Failed to load field jobs.' });
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterPriority, search]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleJobClick = async (job) => {
    try {
      const res = await axios.get(`${API}/api/field/jobs/${job.id}`);
      setDetailJob(res.data);
    } catch {
      setDetailJob(job);
    }
  };

  const boardColumns = ['new', 'assigned', 'en_route', 'in_progress', 'completed'];

  return (
    <div className="space-y-5 animate-fadeIn" data-testid="field-jobs-page">
      {loadError && (
        <div className="flex items-center gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm" data-testid="field-jobs-load-error">
          <AlertTriangle className="h-4 w-4 text-red-600 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-red-700 font-medium">Couldn&apos;t load Field Jobs</p>
            <p className="text-red-600 text-xs">{loadError.message} <span className="text-red-400">(status {String(loadError.code)})</span></p>
          </div>
          <Button size="sm" variant="outline" onClick={fetchAll} data-testid="field-jobs-retry">
            <RefreshCw className="h-4 w-4 mr-1.5" />Retry
          </Button>
        </div>
      )}
      {/* Offline Banner */}
      {!isOnline && (
        <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm" data-testid="offline-banner">
          <WifiOff className="h-4 w-4 text-amber-600" />
          <span className="text-amber-700 font-medium">Offline Mode</span>
          <span className="text-amber-600 text-xs">Changes will sync when connection is restored</span>
          {queueCount > 0 && <Badge className="bg-amber-500 text-white text-[10px] ml-auto">{queueCount} queued</Badge>}
        </div>
      )}
      {isOnline && queueCount > 0 && (
        <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm" data-testid="sync-banner">
          <CloudUpload className="h-4 w-4 text-blue-600" />
          <span className="text-blue-700 text-xs">{queueCount} queued actions ready to sync</span>
          <Button size="sm" variant="outline" className="h-6 text-[10px] ml-auto" onClick={syncQueue} disabled={syncing}>
            {syncing ? 'Syncing...' : 'Sync Now'}
          </Button>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-text-primary">Field Jobs<PageInfoTooltip pageKey="field-jobs" /></h1>
          <p className="text-gray-medium text-sm mt-1">Manage field service jobs, assignments, and completion</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchAll} disabled={loading}>
            <RefreshCw className={cn("h-4 w-4 mr-1.5", loading && "animate-spin")} />Refresh
          </Button>
          <Button size="sm" onClick={() => setShowCreate(true)} data-testid="create-job-btn">
            <Plus className="h-4 w-4 mr-1.5" />New Job
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3">
        <StatCard icon={Briefcase} label="Total" value={stats.total || 0} color="bg-slate-100 text-slate-600" />
        <StatCard icon={ClipboardList} label="New" value={stats.new || 0} color="bg-blue-100 text-blue-600" />
        <StatCard icon={Play} label="In Progress" value={stats.in_progress || 0} color="bg-amber-100 text-amber-600" accent="text-amber-600" />
        <StatCard icon={CheckCircle2} label="Completed" value={stats.completed || 0} color="bg-emerald-100 text-emerald-600" accent="text-emerald-600" />
        <StatCard icon={Timer} label="Avg Duration" value={stats.avg_duration_min ? `${stats.avg_duration_min}m` : '—'} color="bg-violet-100 text-violet-600" />
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
          <Input placeholder="Search jobs..." className="pl-8" value={search} onChange={e => setSearch(e.target.value)} data-testid="job-search-input" />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[150px]" data-testid="status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterPriority} onValueChange={setFilterPriority}>
          <SelectTrigger className="w-[130px]" data-testid="priority-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priorities</SelectItem>
            {Object.entries(PRIORITY_CONFIG).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Tabs value={viewMode} onValueChange={setViewMode} className="hidden sm:block">
          <TabsList className="overflow-x-auto flex-nowrap h-9">
            <TabsTrigger value="list" className="text-xs px-3" data-testid="list-view-btn">List</TabsTrigger>
            <TabsTrigger value="board" className="text-xs px-3" data-testid="board-view-btn">Board</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Job List View */}
      {viewMode === 'list' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {jobs.length === 0 && !loading && (
            <div className="col-span-full text-center py-12" data-testid="field-jobs-empty">
              <Briefcase className="h-10 w-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm mb-1">No field jobs yet</p>
              <p className="text-gray-400 text-xs mb-4">Assign a technician, capture location and status transitions, and link to Field Schedule / GPS in one place.</p>
              <Button size="sm" onClick={() => setShowCreate(true)} data-testid="field-jobs-empty-cta">
                <Plus className="h-4 w-4 mr-1.5" />Create your first job
              </Button>
            </div>
          )}
          {jobs.map(job => (
            <JobCard key={job.id} job={job} onClick={handleJobClick} />
          ))}
        </div>
      )}

      {/* Board View */}
      {viewMode === 'board' && (
        <div className="flex gap-3 overflow-x-auto pb-4">
          {boardColumns.map(status => {
            const cfg = STATUS_CONFIG[status];
            const colJobs = jobs.filter(j => j.status === status);
            return (
              <div key={status} className="min-w-[260px] w-[260px] flex-shrink-0" data-testid={`board-col-${status}`}>
                <div className={cn("rounded-lg px-3 py-1.5 mb-2 flex items-center gap-2", cfg.color)}>
                  <cfg.icon className="h-3.5 w-3.5" />
                  <span className="text-xs font-medium">{cfg.label}</span>
                  <Badge variant="outline" className="ml-auto text-[10px] h-5">{colJobs.length}</Badge>
                </div>
                <div className="space-y-2">
                  {colJobs.map(job => (
                    <JobCard key={job.id} job={job} onClick={handleJobClick} />
                  ))}
                  {colJobs.length === 0 && (
                    <div className="text-center py-6 text-xs text-gray-300">No jobs</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Dialogs */}
      <JobDialog open={showCreate} onClose={() => setShowCreate(false)} onSaved={fetchAll} staff={staff} templates={templates} />
      <JobDialog open={!!editJob} onClose={() => setEditJob(null)} onSaved={fetchAll} job={editJob} staff={staff} templates={templates} />
      <JobDetail job={detailJob} onClose={() => setDetailJob(null)} onRefresh={() => { fetchAll(); setDetailJob(null); }} templates={templates} />
    </div>
  );
}


export default function FieldJobsPage() {
  return (
    <RouteErrorBoundary>
      <FieldJobsPageInner />
    </RouteErrorBoundary>
  );
}
